import { useState } from "react";
import type { MouseEvent } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Link from "@mui/material/Link";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Snackbar from "@mui/material/Snackbar";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import AddIcon from "@mui/icons-material/Add";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { api } from "../api/client";
import type { ClientRow, Meta } from "../api/types";
import { useAuth } from "../auth/AuthProvider";
import { useMutation, useResource } from "../hooks/useResource";
import { EmptyState, ErrorBanner, LoadingState, PageHeader, RoleGate } from "../components/common";

/**
 * Client list. `deliverable` is the column that matters: a client with a
 * subscription and no active channel is subscribed to leads that go nowhere, which
 * is the most common way onboarding is left half-finished.
 */
export function ClientsPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const clients = useResource<{ items: ClientRow[] }>("/api/clients");
  const meta = useResource<Meta>("/api/meta");
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [status, setStatus] = useState("active");
  const [toast, setToast] = useState<string | null>(null);

  const create = useMutation(async () => {
    const created = await api.post<{ id: string }>("/api/clients", { name: name.trim(), status });
    return created;
  });

  if (clients.loading && !clients.data) return <LoadingState label="Loading clients…" />;

  return (
    <Box>
      <PageHeader
        title="Clients"
        subtitle="Subscribers. Open one to manage its markets, zones and delivery channels."
        actions={
          <RoleGate allowed={can("operator")} needs="operator">
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
              Add client
            </Button>
          </RoleGate>
        }
      />

      <ErrorBanner error={clients.error} onRetry={clients.reload} />

      {clients.data && clients.data.items.length === 0 ? (
        <EmptyState title="No clients yet" hint="Add one, then subscribe it to a market." />
      ) : null}

      {clients.data && clients.data.items.length > 0 ? (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Markets</TableCell>
                <TableCell align="right">Channels</TableCell>
                <TableCell align="right">Assigned 24h</TableCell>
                <TableCell align="right">Assigned total</TableCell>
                <TableCell>Deliverable</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clients.data.items.map((client) => (
                <TableRow
                  key={client.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => navigate(`/clients/${client.id}`)}
                >
                  <TableCell>
                    {/* The row stays clickable, but the name is the part that says
                        so — and being a real link is what makes the detail page
                        reachable by keyboard and openable in a new tab. The row's
                        own handler would fire second and navigate twice. */}
                    <Link
                      component={RouterLink}
                      to={`/clients/${client.id}`}
                      onClick={(e: MouseEvent) => e.stopPropagation()}
                    >
                      {client.name}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={client.status}
                      color={client.status === "active" ? "success" : "default"}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    {client.activeSubscriptions}
                    {client.totalSubscriptions !== client.activeSubscriptions
                      ? ` of ${client.totalSubscriptions}`
                      : ""}
                  </TableCell>
                  <TableCell align="right">{client.activeChannels}</TableCell>
                  <TableCell align="right">{client.assigned24h}</TableCell>
                  <TableCell align="right">{client.assignedTotal}</TableCell>
                  <TableCell>
                    {client.deliverable ? (
                      <Chip label="yes" color="success" variant="outlined" />
                    ) : (
                      <Tooltip title="No active delivery channel — assigned leads are recorded but never sent.">
                        <Chip
                          icon={<WarningAmberIcon fontSize="small" />}
                          label="no channel"
                          color="warning"
                          variant="outlined"
                        />
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      ) : null}

      <Dialog open={creating} onClose={() => setCreating(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add client</DialogTitle>
        <DialogContent>
          <ErrorBanner error={create.error} />
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              fullWidth
            />
            <TextField
              select
              label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              fullWidth
            >
              {(meta.data?.clientStatuses ?? ["active", "paused", "churned"]).map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreating(false)} disabled={create.pending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={name.trim() === "" || create.pending}
            onClick={() =>
              void create.run(undefined).then((ok) => {
                if (!ok) return;
                setCreating(false);
                setToast(`Created ${name.trim()}`);
                setName("");
                clients.reload();
              })
            }
          >
            {create.pending ? "Saving…" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={toast !== null} autoHideDuration={4000} onClose={() => setToast(null)} message={toast ?? ""} />
    </Box>
  );
}
